import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  obtenerInvitacionesDocentes,
  reenviarInvitacionesDocentes,
} from '../../../../services/sesionBloquesService';
import { showToast } from '../../../../utils/showToast';

const formatoFecha = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Estado y reenvío de la invitación al grupo de WhatsApp para los docentes
// asignados en el horario de la promoción. El envío automático ocurre una vez
// por docente al asignarlo; aquí coordinación reenvía a quien elija (o a todos).
export default function InvitacionDocentesModal({ open, promocionId, canEdit, onClose }) {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [seleccion, setSeleccion] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await obtenerInvitacionesDocentes(promocionId);
      setDatos(res);
      setSeleccion([]);
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error al cargar invitaciones.' });
    } finally {
      setLoading(false);
    }
  }, [promocionId]);

  useEffect(() => {
    if (!open) return undefined;
    cargar();
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open, cargar]);

  const docentes = useMemo(() => datos?.docentes || [], [datos]);
  // Solo se puede enviar a quien tiene al menos un canal (correo o Campus).
  const enviables = useMemo(
    () => docentes.filter((d) => d.email || d.tiene_campus).map((d) => d.docente_id),
    [docentes]
  );
  const todosMarcados = enviables.length > 0 && enviables.every((id) => seleccion.includes(id));
  const sinLink = !datos?.link_whatsapp;

  const toggle = (id) =>
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleTodos = () => setSeleccion(todosMarcados ? [] : enviables);

  const handleEnviar = async () => {
    if (!seleccion.length) return;
    setEnviando(true);
    try {
      const { resultados = [] } = await reenviarInvitacionesDocentes(promocionId, seleccion);
      const fallidos = resultados.filter((r) => !r.email && !r.campus);
      showToast({
        type: fallidos.length ? 'warning' : 'success',
        message: fallidos.length
          ? `Invitación enviada a ${resultados.length - fallidos.length} docente(s); falló para ${fallidos.map((f) => f.nombre).join(', ')}.`
          : `Invitación enviada a ${resultados.length} docente(s).`,
      });
      cargar();
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error al enviar invitaciones.' });
    } finally {
      setEnviando(false);
    }
  };

  if (!open) return null;

  const renderEstado = (d) => {
    if (!d.email && !d.tiene_campus) {
      return <span className="badge bg-danger-subtle text-danger-emphasis">Sin correo ni Campus</span>;
    }
    if (!d.enviado_at) {
      return <span className="badge bg-warning-subtle text-warning-emphasis">Pendiente</span>;
    }
    return (
      <div className="d-flex flex-column align-items-start gap-1">
        <span className="badge bg-success-subtle text-success-emphasis">
          Enviado {formatoFecha(d.enviado_at)}
          {d.reenvios > 0 && ` · ${d.reenvios} reenvío(s)`}
        </span>
        <span className="small text-muted">
          {d.email && (d.email_enviado
            ? <><i className="bi bi-envelope-check me-1 text-success" />Correo</>
            : <><i className="bi bi-envelope-x me-1 text-danger" />Correo falló</>)}
          {d.email && d.tiene_campus && ' · '}
          {d.tiene_campus && (d.campus_enviado
            ? <><i className="bi bi-bell me-1 text-success" />Campus</>
            : <><i className="bi bi-bell-slash me-1 text-danger" />Campus falló</>)}
        </span>
        {d.link_desactualizado && (
          <span className="badge bg-warning-subtle text-warning-emphasis">Recibió un link anterior</span>
        )}
      </div>
    );
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(15,23,42,0.5)', zIndex: 1055 }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title">
              <i className="bi bi-whatsapp text-success me-2" />Invitación a docentes
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="small text-muted mb-3">
              Cada docente recibe la invitación al grupo una sola vez, al asignarlo en el horario,
              por correo y por notificación del Campus (si tiene cuenta). Si cambias el link
              de la promoción, se reenvía automáticamente.
            </p>

            {sinLink && !loading && (
              <div className="alert alert-warning py-2 small">
                <i className="bi bi-exclamation-triangle me-1" />
                Esta promoción no tiene link de WhatsApp. Agrégalo al editar la promoción para poder enviar invitaciones.
              </div>
            )}

            {loading && (
              <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>
            )}

            {!loading && docentes.length === 0 && (
              <div className="text-center text-muted py-4 small">
                <i className="bi bi-person-x d-block fs-3 mb-2" />
                Aún no hay docentes asignados en el horario de esta promoción.
              </div>
            )}

            {!loading && docentes.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        {canEdit && (
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={todosMarcados}
                            disabled={sinLink || !enviables.length}
                            onChange={toggleTodos}
                            title="Seleccionar todos"
                          />
                        )}
                      </th>
                      <th>Docente</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docentes.map((d) => {
                      const enviable = !!(d.email || d.tiene_campus);
                      return (
                        <tr key={d.docente_id}>
                          <td>
                            {canEdit && (
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={seleccion.includes(d.docente_id)}
                                disabled={sinLink || !enviable}
                                onChange={() => toggle(d.docente_id)}
                              />
                            )}
                          </td>
                          <td>
                            <div className="fw-semibold small">{d.nombre}</div>
                            <div className="small text-muted text-break">
                              {d.email || 'Sin correo registrado'}
                              {!d.tiene_campus && ' · sin cuenta Campus'}
                            </div>
                          </td>
                          <td>{renderEstado(d)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer border-0">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
            {canEdit && (
              <button
                type="button"
                className="btn btn-success"
                disabled={enviando || sinLink || !seleccion.length}
                onClick={handleEnviar}
              >
                {enviando
                  ? <span className="spinner-border spinner-border-sm me-1" role="status" />
                  : <i className="bi bi-send me-1" />}
                {todosMarcados && seleccion.length > 1
                  ? 'Enviar a todos'
                  : `Enviar a ${seleccion.length || ''} seleccionado(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
